import React from "react";

// Ticket #235 — Modal del NPC Comerciante.
//
// Recipe: Atrium Frame (dorado tenue) con grids Slate Card adentro,
// mismo lenguaje que el modal del Banco. Ver CLAUDE.md → "Atrium Frame"
// y "Slate Card".
//
// Memoización: shouldComponentUpdate custom compara solo primitivos
// (hashes + flags + selección + cantidad). setStates del padre que
// no toquen estos props (HP/Mana, chat, etc.) NO re-renderean el modal.
//
// IDs de slot:
//   - Mercader (NPC objs): server escribe 0..N-1 (off-by-one ya
//     contemplado en el render).
//   - Inventario user: convención server 1..24 (slot 0 = "nada").
const TRADE_SLOT_COUNT = 25; // matchea el loop original (0..24)
const INV_SLOT_COUNT = 24;   // matchea swapItems server-side

// Ticket #261: extends Component (NO PureComponent) — `shouldComponentUpdate`
// custom es incompatible con PureComponent (warning de React).
export default class TradeModal extends React.Component {
    shouldComponentUpdate(nextProps) {
        const p = this.props;
        return (
            p.show !== nextProps.show ||
            p.tradeItemsHash !== nextProps.tradeItemsHash ||
            p.userItemsHash !== nextProps.userItemsHash ||
            p.idPosTrade !== nextProps.idPosTrade ||
            p.idPosInv !== nextProps.idPosInv ||
            p.cantTrade !== nextProps.cantTrade ||
            p.gold !== nextProps.gold ||
            p.npcName !== nextProps.npcName
        );
    }

    componentDidMount() {
        document.addEventListener("keydown", this._onKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this._onKeyDown);
    }

    // ESC cierra el modal. Solo si está visible — no nos comemos el ESC
    // global del juego cuando este modal está cerrado.
    _onKeyDown = e => {
        if (!this.props.show) return;
        if (e.key === "Escape" || e.keyCode === 27) {
            e.stopPropagation();
            this.props.onClose();
        }
    };

    _onBackdropClick = e => {
        // Solo cerrar si clickea el backdrop, no el contenido.
        if (e.target === e.currentTarget) {
            this.props.onClose();
        }
    };

    _onQtyChange = e => {
        const raw = e.target.value;
        // Permitimos vacío transitorio (mientras el user está editando).
        if (raw === "") {
            this.props.onQtyChange(1);
            return;
        }
        const n = parseInt(raw, 10);
        if (Number.isFinite(n) && n >= 1) {
            this.props.onQtyChange(n);
        }
    };

    _onQtyMinus = () => {
        const cur = this.props.cantTrade || 1;
        this.props.onQtyChange(Math.max(1, cur - 1));
    };

    _onQtyPlus = () => {
        const cur = this.props.cantTrade || 1;
        this.props.onQtyChange(cur + 1);
    };

    render() {
        const {
            style,
            show,
            tradeItems,
            userItems,
            idPosTrade,
            idPosInv,
            cantTrade,
            gold,
            onSelectTradeSlot,
            onSelectInvSlot,
            onBuy,
            onSell,
            onClose
        } = this.props;

        if (!show) return null;

        // Item seleccionado: prioridad al último selección — derivamos de
        // qué prop tiene el index seteado. idPosTrade es 0-indexed; idPosInv
        // es 1-indexed (slot 0 = sin selección). Para "qué se muestra arriba",
        // privilegiamos el lado más recientemente clickeado vía un flag,
        // pero sin guardarlo en state — usamos el que SÍ tiene item.
        const tradeSel =
            idPosTrade != null && tradeItems[idPosTrade]
                ? tradeItems[idPosTrade]
                : null;
        const invSel =
            idPosInv && userItems[idPosInv] ? userItems[idPosInv] : null;
        const preview = tradeSel || invSel;
        const previewSource = tradeSel ? "trade" : invSel ? "inv" : null;

        const canBuy = !!(tradeSel && cantTrade >= 1 && tradeSel.gold * cantTrade <= gold);
        const canSell = !!(invSel && !invSel.equipped && cantTrade >= 1);

        return (
            <div
                className={style.modalTradeOverlay}
                onClick={this._onBackdropClick}
            >
                <div className={style.modalTrade}>
                    <div className={style.tradeHeader}>
                        <div className={style.tradeTitle}>
                            {this.props.npcName || "Comerciante"}
                        </div>
                        <div
                            className={style.tradeClose}
                            onClick={onClose}
                            title="Cerrar (Esc)"
                        >
                            ×
                        </div>
                    </div>

                    <div className={style.tradeBody}>
                        {/* Preview del item seleccionado. Estado vacío cuando
                            no hay selección — el modal NO colapsa, mantiene
                            su altura estable. */}
                        <div className={style.tradePreview}>
                            <div
                                className={style.tradePreviewImg}
                                style={{
                                    backgroundImage: preview
                                        ? `url("${preview.imgItem}")`
                                        : "none"
                                }}
                            />
                            <div className={style.tradePreviewInfo}>
                                <div className={style.tradePreviewName}>
                                    {preview ? preview.name : "Selecciona un item"}
                                </div>
                                <div className={style.tradePreviewMeta}>
                                    {preview ? preview.info || "" : ""}
                                </div>
                                <div className={style.tradePreviewPrice}>
                                    {preview
                                        ? `${preview.gold} oro` +
                                          (previewSource === "trade"
                                              ? ""
                                              : " · venta")
                                        : ""}
                                </div>
                            </div>
                        </div>

                        <div className={style.tradeGrids}>
                            {/* MERCADER — slots 0..24 */}
                            <div className={style.tradeColumn}>
                                <div className={style.tradeColumnTitle}>
                                    Mercader
                                </div>
                                <div className={style.tradeGrid}>
                                    {Array.from(
                                        { length: TRADE_SLOT_COUNT },
                                        (_, i) => {
                                            const item = tradeItems[i];
                                            const isSelected =
                                                idPosTrade === i;
                                            const slotClass = [
                                                style.tradeSlot,
                                                isSelected
                                                    ? style.tradeSlotSelected
                                                    : "",
                                                item && !item.validUser
                                                    ? style.tradeSlotInvalid
                                                    : ""
                                            ]
                                                .filter(Boolean)
                                                .join(" ");
                                            return (
                                                <div
                                                    key={"t-" + i}
                                                    className={slotClass}
                                                    onClick={() =>
                                                        onSelectTradeSlot(i)
                                                    }
                                                    title={
                                                        item
                                                            ? item.name +
                                                              (item.cant > 1
                                                                  ? ` × ${item.cant}`
                                                                  : "")
                                                            : ""
                                                    }
                                                >
                                                    {item && (
                                                        <>
                                                            <div
                                                                className={
                                                                    style.tradeSlotImg
                                                                }
                                                                style={{
                                                                    backgroundImage: `url("${item.imgItem}")`
                                                                }}
                                                            />
                                                            {item.cant > 1 ? (
                                                                <span
                                                                    className={
                                                                        style.tradeSlotCant
                                                                    }
                                                                >
                                                                    {item.cant}
                                                                </span>
                                                            ) : null}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        }
                                    )}
                                </div>
                            </div>

                            {/* INVENTARIO — slots 1..24 (server convention) */}
                            <div className={style.tradeColumn}>
                                <div className={style.tradeColumnTitle}>
                                    Inventario
                                </div>
                                <div className={style.tradeGrid}>
                                    {Array.from(
                                        { length: INV_SLOT_COUNT },
                                        (_, idx) => {
                                            const i = idx + 1;
                                            const item = userItems[i];
                                            const isSelected =
                                                idPosInv === i;
                                            const isEquipped = !!(
                                                item && item.equipped
                                            );
                                            const slotClass = [
                                                style.tradeSlot,
                                                isSelected
                                                    ? style.tradeSlotSelected
                                                    : "",
                                                isEquipped
                                                    ? style.tradeSlotDisabled
                                                    : ""
                                            ]
                                                .filter(Boolean)
                                                .join(" ");
                                            return (
                                                <div
                                                    key={"i-" + i}
                                                    className={slotClass}
                                                    onClick={() =>
                                                        onSelectInvSlot(i)
                                                    }
                                                    title={
                                                        item
                                                            ? item.name +
                                                              (item.cant > 1
                                                                  ? ` × ${item.cant}`
                                                                  : "") +
                                                              (isEquipped
                                                                  ? " (equipado)"
                                                                  : "")
                                                            : ""
                                                    }
                                                >
                                                    {item && (
                                                        <>
                                                            <div
                                                                className={
                                                                    style.tradeSlotImg
                                                                }
                                                                style={{
                                                                    backgroundImage: `url("${item.imgItem}")`
                                                                }}
                                                            />
                                                            {item.cant > 1 ? (
                                                                <span
                                                                    className={
                                                                        style.tradeSlotCant
                                                                    }
                                                                >
                                                                    {item.cant}
                                                                </span>
                                                            ) : null}
                                                            {isEquipped ? (
                                                                <span
                                                                    className={
                                                                        style.tradeSlotEquipped
                                                                    }
                                                                >
                                                                    E
                                                                </span>
                                                            ) : null}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        }
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={style.tradeActions}>
                        <button
                            type="button"
                            className={`${style.tradeBtn} ${
                                style.tradeBtnPrimary
                            }`}
                            disabled={!canBuy}
                            onClick={onBuy}
                            title={
                                tradeSel
                                    ? `Comprar ${cantTrade} × ${tradeSel.name}`
                                    : "Seleccioná un item del Mercader"
                            }
                        >
                            Comprar
                        </button>
                        <div className={style.tradeQtyWrap}>
                            <button
                                type="button"
                                className={style.tradeQtyBtn}
                                onClick={this._onQtyMinus}
                                disabled={cantTrade <= 1}
                                aria-label="Menos cantidad"
                            >
                                −
                            </button>
                            <input
                                type="number"
                                min="1"
                                className={style.tradeQtyInput}
                                value={cantTrade}
                                onChange={this._onQtyChange}
                                data-game-key="false"
                            />
                            <button
                                type="button"
                                className={style.tradeQtyBtn}
                                onClick={this._onQtyPlus}
                                aria-label="Más cantidad"
                            >
                                +
                            </button>
                        </div>
                        <button
                            type="button"
                            className={`${style.tradeBtn} ${
                                style.tradeBtnPrimary
                            }`}
                            disabled={!canSell}
                            onClick={onSell}
                            title={
                                invSel
                                    ? invSel.equipped
                                        ? "Desequipá el item antes de venderlo"
                                        : `Vender ${cantTrade} × ${invSel.name}`
                                    : "Seleccioná un item de tu Inventario"
                            }
                        >
                            Vender
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
